import type React from "react"
import { useState, useEffect } from "react"
import { useParams, useNavigate, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { saveAs } from "file-saver"
import {
  User,
  Car,
  DollarSign,
  MessageSquare,
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  X,
  Check,
  Upload,
  Download,
  Eye,
  AlertTriangle,
  Calendar,
  Clock,
  FileText,
  Briefcase,
  Edit,
} from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import api from "../../utils/api"
import Chat from "../../components/Chat"
import ConfirmationModal from "../../components/ConfirmationModal"
import AIServiceInfo from "../../components/AIServiceInfo"

interface Order {
  id: number
  client_id: number
  vehicle_id: number
  service_id: number
  is_ai: number
  ai_prompt: string
  ai_audio: string
  technicien_id: number | null
  status: string
  description: string
  created_at: string
  updated_at: string
  scheduled_at: string
  client: {
    first_name: string
    last_name: string
    email: string
    phone: string
    avatar: string
  }
  company: {
    raison_social: string
    email: string
    phone: string
    avatar: string
    latitude: number | null
    longitude: number | null
  }
  vehicle: {
    brand: {
      name: string
      icon: string
    }
    model: string
    year: number
    plate_number: string
  }
  service: {
    name: string
    base_price: number
  }
  technician: {
    first_name: string
    last_name: string
    avatar: string
  } | null
  final_price: number
  invoice: {
    id: number
    file_path: string
  }
}

interface Technician {
  id: number
  first_name: string
  last_name: string
  avatar: string
}

export default function OrderDetails() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()

  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [technicians, setTechnicians] = useState<Technician[]>([])
  const [selectedTechnician, setSelectedTechnician] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState(searchParams.get("open_chat") ? "chat" : "details")
  const [toast, setToast] = useState({ visible: false, severity: "", message: "" })
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [confirmationModal, setConfirmationModal] = useState({ isOpen: false, action: "", title: "", message: "" })
  const [repairState, setRepairState] = useState<"good" | "bad" | "mid" | "">("good")
  const [repairNote, setRepairNote] = useState("")
  const navigate = useNavigate()
  const { t } = useTranslation()

  useEffect(() => {
    if (id) {
      fetchOrderDetails(Number.parseInt(id))
      fetchTechnicians()
    }
  }, [id])

  const fetchOrderDetails = async (orderId: number) => {
    try {
      const orderResponse = await api.get(`/service-orders/${orderId}`)
      setOrder(orderResponse.data)
      setSelectedTechnician(orderResponse.data.technicien_id)
      setLoading(false)
    } catch (error) {
      console.error("Error fetching order details:", error)
      showToast("error", t("error_fetching_order_details"))
      setLoading(false)
    }
  }

  const fetchTechnicians = async () => {
    try {
      const response = await api.get("/technicians")
      setTechnicians(response.data.data)
    } catch (error) {
      console.error("Error fetching technicians:", error)
    }
  }

  const handleStatusChange = async (newStatus: string) => {
    if (order && newStatus !== order.status) {
      if (newStatus === "cancelled" || newStatus === "completed") {
        setRepairState("good") // Reset repair state
        setRepairNote("") // Reset repair note
        setConfirmationModal({
          isOpen: true,
          action: newStatus,
          title: t(`confirm_${newStatus}_title`),
          message: t(`confirm_${newStatus}_message`),
        })
      } else {
        await updateOrderStatus(newStatus)
      }
    }
  }

  const updateOrderStatus = async (newStatus: string) => {
    try {
      await api.put(`/service-orders/${order!.id}`, {
        status: newStatus,
        repair_state: repairState,
        repair_note: repairNote,
      })
      setOrder({ ...order!, status: newStatus })
      showToast("success", t("status_updated"))
      setRepairState("good")
      setRepairNote("")
    } catch (error) {
      console.error("Error updating order status:", error)
      showToast("error", t("error_updating_status"))
    }
  }

  const handleConfirmStatusChange = async () => {
    try {
      if (confirmationModal.action === "completed") {
        if (!repairState) {
          showToast("error", t("please_select_repair_state"))
          return
        }
        await api.post(`/service-orders/${order!.id}/complete`, {
          repair_note: repairNote,
          repair_state: repairState,
        })
        setOrder({ ...order!, status: "completed" })
        showToast("success", t("order_completed"))
      } else if (confirmationModal.action === "cancelled") {
        if (!repairNote) {
          showToast("error", t("please_enter_repair_note"))
          return
        }
        await api.post(`/service-orders/${order!.id}/cancel`, {
          repair_note: repairNote,
        })
        setOrder({ ...order!, status: "cancelled" })
        showToast("success", t("order_cancelled"))
      }
      setConfirmationModal({ ...confirmationModal, isOpen: false })
      setRepairState("good")
      setRepairNote("")
    } catch (error) {
      console.error(`Error ${confirmationModal.action} order:`, error)
      showToast("error", t(`error_${confirmationModal.action}_order`))
    }
  }

  const handleTechnicianAssign = async () => {
    if (order && selectedTechnician !== order.technicien_id) {
      try {
        await api.put(`/service-orders/${order.id}`, { technicien_id: selectedTechnician })
        setOrder({ ...order, technicien_id: selectedTechnician })
        showToast("success", t("technician_assigned"))
      } catch (error) {
        console.error("Error assigning technician:", error)
        showToast("error", t("error_assigning_technician"))
      }
    }
  }

  const handleUploadInvoice = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (order && event.target.files && event.target.files[0]) {
      const file = event.target.files[0]
      const formData = new FormData()
      formData.append("invoice", file)

      try {
        await api.post(`/service-orders/${order.id}/invoice`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        showToast("success", t("invoice_uploaded"))
        fetchOrderDetails(order.id)
      } catch (error) {
        console.error("Error uploading invoice:", error)
        showToast("error", t("error_uploading_invoice"))
      }
    }
  }

  const fetchAndOpenInvoicePDF = async () => {
    if (order && order.invoice && order.invoice.file_path) {
      try {
        const token = localStorage.getItem("authToken")
        const response = await fetch(`${process.env.REACT_APP_API_URL}/${order.invoice.file_path}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error("Failed to fetch PDF")
        }

        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        setPdfUrl(url)
        setModalIsOpen(true)
      } catch (error) {
        console.error("Error fetching PDF:", error)
        showToast("error", t("error_fetching_invoice"))
      }
    }
  }

  const downloadInvoicePDF = async () => {
    if (order && order.invoice && order.invoice.file_path) {
      try {
        const token = localStorage.getItem("authToken")
        const response = await fetch(`${process.env.REACT_APP_API_URL}/${order.invoice.file_path}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })

        if (!response.ok) {
          throw new Error("Failed to fetch PDF")
        }

        const blob = await response.blob()
        saveAs(blob, `invoice_${order.id}.pdf`)
      } catch (error) {
        console.error("Error downloading PDF:", error)
        showToast("error", t("error_downloading_invoice"))
      }
    }
  }

  const closeModal = () => {
    setModalIsOpen(false)
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl)
      setPdfUrl(null)
    }
  }

  const showToast = (severity: string, message: string) => {
    setToast({ visible: true, severity, message })
    setTimeout(() => setToast({ ...toast, visible: false }), 3000)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  if (!order) {
    return <div className="text-center text-2xl font-bold mt-8">{t("order_not_found")}</div>
  }

  return (
    <div className="bg-gradient-to-br from-gray-100 to-gray-200 min-h-screen p-4 md:p-8">
      <AnimatePresence>
        {toast.visible && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-4 right-4 p-4 rounded-md shadow-md ${
              toast.severity === "success" ? "bg-green-500" : "bg-red-500"
            } text-white z-50`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white rounded-xl shadow-2xl p-6 mb-6"
      >
        <div className="flex justify-between items-center">
          <div className="flex-grow ">
            <button
              onClick={() => navigate("/orders")}
              className="p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 "
            >
              <ArrowLeft size={24} className="text-gray-600" />
            </button>
          </div>
          <h1 className="text-3xl font-bold flex  items-center text-gray-800">
            {t("order")} #{order.id}
            <span
              className={`ml-4 px-4 py-1 rounded-full text-sm font-semibold ${
                order.status === "completed"
                  ? "bg-green-100 text-green-800"
                  : order.status === "in_progress"
                    ? "bg-blue-100 text-blue-800"
                    : order.status === "cancelled"
                      ? "bg-red-100 text-red-800"
                      : "bg-yellow-100 text-yellow-800"
              }`}
            >
              {t(`order_status_${order.status}`)}
            </span>
          </h1>
          <div className="flex-fit flex pl-3 gap-2">
            {order.status === "pending" && (
              <>
                <ActionButton
                  onClick={() => handleStatusChange("in_progress")}
                  icon={<Check size={16} />}
                  label={t("accept")}
                  color="blue"
                />
                <ActionButton
                  onClick={() => handleStatusChange("cancelled")}
                  icon={<X size={16} />}
                  label={t("cancel")}
                  color="red"
                />
              </>
            )}
            {order.status === "in_progress" && (
              <>
                <ActionButton
                  onClick={() => handleStatusChange("completed")}
                  icon={<Check size={16} />}
                  label={t("complete")}
                  color="green"
                />
                <ActionButton
                  onClick={() => handleStatusChange("cancelled")}
                  icon={<X size={16} />}
                  label={t("cancel")}
                  color="red"
                />
              </>
            )}
          </div>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <InfoCard
          icon={<Briefcase className="text-blue-500" size={24} />}
          title={t("client_information")}
          content={
            <div className="flex items-center">
              <img
                src={`${process.env.REACT_APP_PIC_STORAGE}/${order.company.avatar}`}
                alt={`${order.company.raison_social}`}
                className="w-16 h-16 rounded-full mr-4 border-2 border-blue-500"
              />
              <div>
                <p className="font-bold text-lg text-gray-800">{order.company.raison_social}</p>
                <p className="flex items-center text-sm text-gray-600 mt-1">
                  <Mail size={16} className="mr-2 text-blue-500" />
                  {order.company.email}
                </p>
                <p className="flex items-center text-sm text-gray-600 mt-1">
                  <Phone size={16} className="mr-2 text-blue-500" />
                  {order.company.phone}
                </p>
              </div>
            </div>
          }
        />
        <InfoCard
          icon={<Car className="text-blue-500" size={24} />}
          title={t("vehicle_information")}
          content={
            <>
              <p className="flex items-center text-lg text-gray-800">
                <Car size={20} className="mr-3 text-blue-500" />
                {order.vehicle.brand.name} {order.vehicle.model}{" "}
                <span className="ml-2 text-sm text-gray-600 font-bold">{order.vehicle.year}</span>
              </p>
              <p className="mt-3 flex items-center text-gray-700">
                <MapPin size={20} className="mr-3 text-blue-500" />
                {t("plate_number")}: <span className="font-semibold ml-2">{order.vehicle.plate_number}</span>
              </p>
            </>
          }
        />
        <InfoCard
          icon={<DollarSign className="text-blue-500" size={24} />}
          title={t("service_information")}
          content={
            <>
              <p className="text-lg mb-3 flex items-center text-gray-800">
                <Clock size={20} className="mr-3 text-blue-500" />
                {order?.service?.name || t("deleted")}
              </p>
              <p className="mt-2 flex items-center text-lg text-gray-700">
                <DollarSign size={20} className="mr-3 text-blue-500" />
                {t("base_price")}:{" "}
                <span className="font-semibold ml-2">{order?.service?.base_price || t("deleted")} MAD</span>
              </p>
              <p className="mt-2 flex items-center text-lg text-gray-700">
                <DollarSign size={20} className="mr-3 text-blue-500" />
                {t("final_price")}: <span className="font-semibold ml-2">{order.final_price} MAD</span>
              </p>

              <AIServiceInfo isAI={order.is_ai === 1} prompt={order.ai_prompt} audioUrl={order.ai_audio} />
            </>
          }
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="bg-white rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="border-b border-gray-200">
          <nav className="flex">
            <TabButton
              active={activeTab === "details"}
              onClick={() => setActiveTab("details")}
              icon={<FileText size={20} />}
              label={t("details")}
            />
            <TabButton
              active={activeTab === "chat"}
              onClick={() => setActiveTab("chat")}
              icon={<MessageSquare size={20} />}
              label={t("chat")}
            />
          </nav>
        </div>

        <div className="p-6">
          {activeTab === "details" && (
            <div>
              <h3 className="text-2xl font-semibold mb-6 flex items-center text-gray-800">
                <Calendar className="mr-2 text-blue-500" size={24} />
                {t("order_management")}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <h4 className="text-lg font-medium text-gray-700 mb-2">{t("assign_technician")}</h4>
                    <div className="flex">
                      <select
                        id="technician"
                        value={selectedTechnician || ""}
                        onChange={(e) => setSelectedTechnician(Number(e.target.value))}
                        className="flex-grow p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg bg-white"
                      >
                        <option value="">{t("select_technician")}</option>
                        {technicians.map((tech) => (
                          <option key={tech.id} value={tech.id}>{`${tech.first_name} ${tech.last_name}`}</option>
                        ))}
                      </select>
                      <button
                        onClick={handleTechnicianAssign}
                        className="ml-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200"
                      >
                        <User size={20} />
                      </button>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-lg font-medium text-gray-700 mb-2">{t("invoice")}</h4>
                    <div className="flex items-center space-x-4">
                      <label
                        htmlFor="invoice-upload"
                        className="cursor-pointer bg-blue-500 text-white px-6 py-3 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition duration-200 text-lg flex items-center justify-center"
                      >
                        <Upload size={20} className="mr-2" />
                        {t("upload_invoice")}
                      </label>
                      <input
                        id="invoice-upload"
                        type="file"
                        accept="application/pdf"
                        onChange={handleUploadInvoice}
                        className="hidden"
                      />
                      {order.invoice && order.invoice.file_path ? (
                        <div className="flex space-x-2">
                          <ActionButton
                            onClick={fetchAndOpenInvoicePDF}
                            icon={<Eye size={20} />}
                            label={t("view_invoice")}
                            color="green"
                          />
                          <ActionButton
                            onClick={downloadInvoicePDF}
                            icon={<Download size={20} />}
                            label={t("download_invoice")}
                            color="blue"
                          />
                        </div>
                      ) : (
                        <p className="text-gray-500">{t("no_invoice_uploaded")}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-medium text-gray-700 mb-2">{t("order_description")}</h4>
                  <div className="relative">
                    <textarea
                      value={order.description}
                      readOnly
                      rows={5}
                      className="w-full p-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg resize-none mb-4 bg-gray-50"
                    />
                    <button
                      onClick={() => {
                        /* Add edit functionality */
                      }}
                      className="absolute top-2 right-2 p-2 bg-gray-200 rounded-full hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "chat" && <Chat order={order} />}
        </div>
      </motion.div>

      {modalIsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-5/6"
          >
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-800">{t("view_invoice")}</h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded-full p-1"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-6 h-5/6">
              {pdfUrl && <iframe src={pdfUrl} title="Invoice PDF" className="w-full h-full border-none" />}
            </div>
          </motion.div>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        onConfirm={handleConfirmStatusChange}
        title={confirmationModal.title}
        message={confirmationModal.message}
      >
        <>
          {confirmationModal.action === "completed" && (
            <div className="mb-4">
              <label className="text-lg font-medium text-gray-700 mb-2">{t("repair_state")}</label>
              <div className="flex space-x-4 justify-center">
                {["good", "mid", "bad"].map((state) => (
                  <label key={state} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="radio"
                      name="repairState"
                      value={state}
                      checked={repairState === state}
                      onChange={(e) => setRepairState(e.target.value as "good" | "mid" | "bad")}
                      className="sr-only"
                    />
                    <div
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-3 ${
                        repairState === state
                          ? state === "good"
                            ? "bg-green-500 border-green-600"
                            : state === "mid"
                              ? "bg-yellow-500 border-yellow-600"
                              : "bg-red-500 border-red-600"
                          : "border-gray-300"
                      }`}
                    >
                      {repairState === state && <div className="w-3 h-3 rounded-full bg-white" />}
                    </div>
                    <span className="text-gray-700 capitalize">{t(state)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          <div className="mb-4">
            <label htmlFor="repairNote" className="text-lg font-medium text-gray-700 mb-4">
              {t("repair_note")}
            </label>
            <textarea
              id="repairNote"
              value={repairNote}
              onChange={(e) => setRepairNote(e.target.value)}
              rows={3}
              className="w-full p-4 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg resize-none mb-4 bg-gray-50 mt-3"
              placeholder={t("enter_repair_note")}
            />
          </div>
        </>
      </ConfirmationModal>
    </div>
  )
}

const InfoCard: React.FC<{ icon: React.ReactNode; title: string; content: React.ReactNode }> = ({
  icon,
  title,
  content,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="bg-white rounded-xl shadow-lg p-6 transition duration-300 hover:shadow-xl"
  >
    <h2 className="text-xl font-semibold mb-4 border-b pb-2 flex items-center text-gray-800">
      {icon}
      <span className="ml-2">{title}</span>
    </h2>
    <div className="text-gray-700">{content}</div>
  </motion.div>
)

const TabButton: React.FC<{ active: boolean; onClick: () => void; icon: React.ReactNode; label: string }> = ({
  active,
  onClick,
  icon,
  label,
}) => (
  <button
    className={`${
      active
        ? "border-blue-500 text-blue-600"
        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
    } flex items-center space-x-2 py-4 px-6 border-b-2 font-medium text-lg transition duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50`}
    onClick={onClick}
  >
    {icon}
    <span>{label}</span>
  </button>
)

const ActionButton: React.FC<{ onClick: () => void; icon: React.ReactNode; label: string; color: string }> = ({
  onClick,
  icon,
  label,
  color,
}) => (
  <button
    onClick={onClick}
    className={`px-4 py-2 bg-${color}-500 text-white rounded-md hover:bg-${color}-600 focus:outline-none focus:ring-2 focus:ring-${color}-500 focus:ring-opacity-50 transition duration-200 flex items-center space-x-2`}
  >
    {icon}
    <span>{label}</span>
  </button>
)

